import {
  Box,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactElement } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Copy } from 'react-feather';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import Skeleton from '../../component/skeleton/Skeleton';
import toastOptions from '../../helpers/toastOptions';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import { TOTAL_SERIES, getEquipmentSummary, getJobHistory, getUtilizationSeries, jobStats, utilizationStats } from '../../redux/slices/tools/toolsSlice';
import { CardStyled } from '../../styled/components';
import { UtilizationChart } from './UtilizationChart';
import { NOT_TRACKED_STATUSES, TRACKED_STATUSES, itemsLink } from './itemsLink';

const formatPct = (value: number): string => `${value.toFixed(value < 10 && value > 0 ? 1 : 0)}%`;

// Dense cells: ten numeric columns share the row with the chart, so the table
// gives up horizontal padding and a point of type rather than columns, and the
// wrapper scrolls rather than letting the table bleed over the chart on a
// narrow screen.
const cellSx = { py: 1.75, px: 0.5, fontSize: 12, whiteSpace: 'nowrap' };
const headSx = { ...cellSx, fontSize: 11 };
// The group band: same size as the column names but set apart, so the eye reads
// it as a heading over them rather than as another column.
const groupSx = { ...headSx, py: 0.75, fontWeight: 'fontWeightBold', color: 'text.secondary', borderBottom: 'none' };
// A rule down the left of each group's first column, carried through the header
// and every body row, so the two groups read as blocks rather than as one run
// of eight numbers.
const groupEdge = { borderLeft: 1, borderLeftColor: 'divider' };

// Numbers are links into the item list, carrying the row's category and the
// tracking state the column represents.
const CountLink = ({ to, children }: { to: string; children: ReactElement | string }): ReactElement => (
  <Link component={RouterLink} to={to} underline="hover" color="inherit" onClick={(event) => event.stopPropagation()}>
    {children}
  </Link>
);

type EquipmentSummaryProps = { fetching: boolean; historyFetching: boolean; selected: string; onSelect: (category: string) => void };

// Org-wide equipment count with a per-product-category breakdown of how much of
// it is currently tracked. Tracked means the item is at a job site, in use or
// left behind. Max and Avg are that same share at its highest and its mean over
// the past 12 months, from the series the chart plots, so today's figure can be
// read against the category's own history.
//
// Selecting a category row drives the utilization chart beside it; Total is the
// default and the whole-organization series.
export const EquipmentSummary = ({ fetching, historyFetching, selected, onSelect }: EquipmentSummaryProps): ReactElement => {
  const term = useMarketTerm();
  const summary = useSelector(getEquipmentSummary);
  const series = useSelector(getUtilizationSeries);
  const jobs = useSelector(getJobHistory)();

  const categoryStats = (category: string) => utilizationStats(series.byCategory[category]);
  const categoryJobs = (category: string) => jobStats(jobs[category]);
  // The last point in the series is today, the same value the chart's subtitle
  // reports when it is switched to jobs.
  const jobsToday = (category: string) => {
    const points = jobs[category];
    return points?.length ? points[points.length - 1].jobCount : undefined;
  };

  const rowSx = (category: string) => ({
    cursor: 'pointer',
    ...(selected === category && { backgroundColor: 'action.selected' }),
  });

  // The table as tab-separated text, header first and Total last, so a paste
  // into a spreadsheet lands one value per cell.
  const tableText = (): string => {
    const onSite = term('Tracked');
    const atHome = term('Not Tracked');
    const header = [
      'Product Category',
      'Total',
      `${onSite} Cnt`,
      `${onSite} %`,
      `${onSite} Max %`,
      `${onSite} Avg %`,
      `${atHome} Cnt`,
      `${atHome} %`,
      'Jobs Cnt',
      'Jobs Max',
      'Jobs Avg',
    ];
    const line = (name: string, total: number, tracked: number, trackedPct: number, notTracked: number, notTrackedPct: number): string => {
      const stats = categoryStats(name);
      const jobs = categoryJobs(name);
      const today = jobsToday(name);
      return [
        name,
        total,
        tracked,
        formatPct(trackedPct),
        stats?.max !== undefined ? formatPct(stats.max) : '',
        stats?.avg !== undefined ? formatPct(stats.avg) : '',
        notTracked,
        formatPct(notTrackedPct),
        today ?? '',
        jobs ? jobs.max : '',
        jobs ? jobs.avg.toFixed(1) : '',
      ].join('\t');
    };
    return [
      header.join('\t'),
      ...summary.categories.map((row) => line(row.category, row.total, row.tracked, row.trackedPct, row.notTracked, row.notTrackedPct)),
      line(TOTAL_SERIES, summary.total, summary.tracked, summary.trackedPct, summary.notTracked, summary.notTrackedPct),
    ].join('\n');
  };

  return (
    <CardStyled sx={{ mb: 6, p: 6 }}>
      <Typography variant="h4">Equipment</Typography>

      <Stack direction="row" spacing={8} alignItems="baseline" sx={{ mt: 2 }}>
        <Box>
          <Typography variant="h2" component="div">
            {fetching ? <Skeleton width={80} /> : summary.total.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All Equipment
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4" component="div">
            {fetching ? <Skeleton width={60} /> : `${summary.tracked.toLocaleString()} (${formatPct(summary.trackedPct)})`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {term('Tracked')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4" component="div">
            {fetching ? <Skeleton width={60} /> : `${summary.notTracked.toLocaleString()} (${formatPct(summary.notTrackedPct)})`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {term('Not Tracked')}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mt: 3, mb: 1 }} />

      {fetching && <LinearProgress />}

      <Grid container spacing={6} alignItems="stretch">
        <Grid item xs={12} lg={6}>
          <Box sx={{ overflowX: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h6">Equipment by Category</Typography>
              <Tooltip arrow placement="top" title="Copy table">
                <span>
                  <CopyToClipboard text={tableText()} onCopy={() => toast.success('Equipment by Category copied', toastOptions)}>
                    <IconButton size="small" aria-label="Copy Equipment by Category" sx={{ p: 0.5 }} disabled={!summary.categories.length}>
                      <Copy size={14} />
                    </IconButton>
                  </CopyToClipboard>
                </span>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {term('Tracked')} now, with the past 12 months for comparison
            </Typography>

            <Table size="small">
              <TableHead>
                {/* Two header rows: a group band over the measures that share a
                    subject, and short names underneath, so "Max %" and "Max" do
                    not each have to repeat what they are a maximum of. Columns
                    that belong to no group sit on the lower row with the rest of
                    the column names. */}
                <TableRow>
                  <TableCell sx={{ ...headSx, borderBottom: 'none' }} colSpan={2} />
                  <TableCell sx={{ ...groupSx, ...groupEdge }} align="center" colSpan={4}>
                    {term('Tracked')}
                  </TableCell>
                  <TableCell sx={{ ...groupSx, ...groupEdge }} align="center" colSpan={2}>
                    {term('Not Tracked')}
                  </TableCell>
                  <TableCell sx={{ ...groupSx, ...groupEdge }} align="center" colSpan={3}>
                    Jobs
                  </TableCell>
                </TableRow>
                <TableRow>
                  {/* No fixed width: the category names are short, and the table
                      compresses to its content beside the chart. */}
                  <TableCell sx={headSx}>Product Category</TableCell>
                  <TableCell sx={headSx} align="right">
                    Total
                  </TableCell>
                  <TableCell sx={{ ...headSx, ...groupEdge }} align="right">
                    Cnt
                  </TableCell>
                  <TableCell sx={headSx} align="right">
                    %
                  </TableCell>
                  <TableCell sx={headSx} align="right">
                    Max %
                  </TableCell>
                  <TableCell sx={headSx} align="right">
                    Avg %
                  </TableCell>
                  <TableCell sx={{ ...headSx, ...groupEdge }} align="right">
                    Cnt
                  </TableCell>
                  <TableCell sx={headSx} align="right">
                    %
                  </TableCell>
                  <TableCell sx={{ ...headSx, ...groupEdge }} align="right">
                    Cnt
                  </TableCell>
                  <TableCell sx={headSx} align="right">
                    Max
                  </TableCell>
                  <TableCell sx={headSx} align="right">
                    Avg
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!fetching && !summary.categories.length && (
                  <TableRow>
                    <TableCell sx={cellSx} colSpan={11}>
                      No equipment found.
                    </TableCell>
                  </TableRow>
                )}
                {summary.categories.map((row) => (
                  <TableRow key={row.category} hover selected={selected === row.category} sx={rowSx(row.category)} onClick={() => onSelect(row.category)}>
                    <TableCell sx={cellSx}>
                      <Link component="button" type="button" underline="hover" sx={{ fontSize: 13 }} onClick={() => onSelect(row.category)}>
                        {row.category}
                      </Link>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <CountLink to={itemsLink({ category: row.category })}>{row.total.toLocaleString()}</CountLink>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <CountLink to={itemsLink({ category: row.category, scanStatus: TRACKED_STATUSES })}>{row.tracked.toLocaleString()}</CountLink>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {formatPct(row.trackedPct)}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {categoryStats(row.category)?.max !== undefined ? formatPct(categoryStats(row.category)!.max) : '—'}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {categoryStats(row.category)?.avg !== undefined ? formatPct(categoryStats(row.category)!.avg) : '—'}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <CountLink to={itemsLink({ category: row.category, scanStatus: NOT_TRACKED_STATUSES })}>{row.notTracked.toLocaleString()}</CountLink>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {formatPct(row.notTrackedPct)}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {jobsToday(row.category) !== undefined ? jobsToday(row.category)!.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {categoryJobs(row.category) ? categoryJobs(row.category)!.max.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {categoryJobs(row.category) ? categoryJobs(row.category)!.avg.toFixed(1) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!!summary.categories.length && (
                  <TableRow hover selected={selected === TOTAL_SERIES} sx={rowSx(TOTAL_SERIES)} onClick={() => onSelect(TOTAL_SERIES)}>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }}>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        sx={{ fontSize: 13, fontWeight: 'fontWeightBold' }}
                        onClick={() => onSelect(TOTAL_SERIES)}
                      >
                        {TOTAL_SERIES}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      <CountLink to={itemsLink({})}>{summary.total.toLocaleString()}</CountLink>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      <CountLink to={itemsLink({ scanStatus: TRACKED_STATUSES })}>{summary.tracked.toLocaleString()}</CountLink>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {formatPct(summary.trackedPct)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {categoryStats(TOTAL_SERIES)?.max !== undefined ? formatPct(categoryStats(TOTAL_SERIES)!.max) : '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {categoryStats(TOTAL_SERIES)?.avg !== undefined ? formatPct(categoryStats(TOTAL_SERIES)!.avg) : '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      <CountLink to={itemsLink({ scanStatus: NOT_TRACKED_STATUSES })}>{summary.notTracked.toLocaleString()}</CountLink>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {formatPct(summary.notTrackedPct)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {jobsToday(TOTAL_SERIES) !== undefined ? jobsToday(TOTAL_SERIES)!.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {categoryJobs(TOTAL_SERIES) ? categoryJobs(TOTAL_SERIES)!.max.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                      {categoryJobs(TOTAL_SERIES) ? categoryJobs(TOTAL_SERIES)!.avg.toFixed(1) : '—'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <UtilizationChart fetching={historyFetching} category={selected} points={series.byCategory[selected] || []} jobs={jobs[selected]} />
        </Grid>
      </Grid>
    </CardStyled>
  );
};
